import {Cart, CartFactory} from "@connox/typescript-library";
import {CurrencyCode} from "@connox/typescript-library/types/cart/CurrencyCode";
import * as React from "react";
import {useEffect, useState} from "react";
import * as Sentry from "@sentry/browser";
import {Severity} from "@sentry/browser";
import {UrlSearchParamsHelper} from "../../../helpers/urlSearchParamsHelper";
import {useCustomerContext} from "../../customer/CustomerContextProvider/CustomerContextProvider";
import {getShopId} from "../../lib/getShopId";
import {isAmazonLoggedIn} from "../../lib/payments/AmazonPayLibrary";
import window from "../../lib/WindowExtended";
import {IProductTrackingData, Tracking} from "../../../tracking/tracking";

interface ICurrencySelector extends HTMLFormElement {
  CurrencyCode: HTMLSelectElement;
}

export interface IPartials {
  partialAddress: {
    email: string;
    firstName: string;
    lastName: string;
    gender: "M" | "F";
  };
  isGuest: boolean;
  isCorporateCustomer: boolean;
}

export interface IViewState {
  partials?: IPartials;
  isRegistering: boolean;
}

interface ICartContext {
  cart: Cart;

  isIntoCheckout: boolean;
  setIsIntoCheckout: (isIntoCheckout: boolean) => void;

  isCartVisible: boolean;
  setIsCartVisible: (isCartVisible: boolean) => void;

  itemsAdded: number[];
  setItemsAdded: (itemIds: number[]) => void;

  setViewState: (viewState?: IViewState) => void;
  viewState: IViewState;

  setVoucherAdded: (voucherAdded: string) => void;
  voucherAdded: string;
}

interface IRequestCart {
  cart: Cart;
  done: () => void;
}

export interface ICartContextNoCart {
  requestCart: () => Promise<IRequestCart>;

  isCartVisible: boolean;
  setIsCartVisible: (isCartVisible: boolean) => void;

  setItemsAdded: (itemIds: number[]) => void;
  itemsAdded: number[];

  setVoucherAdded: (voucherAdded: string) => void;
  voucherAdded: string;
}

type IUnsafeCartContext = ICartContext | ICartContextNoCart | null;

const unsafeCartContext = React.createContext<IUnsafeCartContext>(null);

export const isCart = (cart: IUnsafeCartContext): cart is ICartContext => {
  return (cart as ICartContext).cart !== undefined;
};

export const useCartContext = (): ICartContext => {
  const cartContext = React.useContext(unsafeCartContext);
  if (!isCart(cartContext)) {
    throw new Error("useCartContext must be used in a component within a CartContextProvider.");
  }

  return cartContext;
};

export const useUnsafeCartContext = (): IUnsafeCartContext => {
  return React.useContext(unsafeCartContext);
};

const CartContextProvider: React.FC = (props) => {
  const setItemsAdded = (itemsAdded: number[]) => {
    setState((prevState) => {
      if (!prevState) {
        return null;
      }

      return {...prevState, itemsAdded};
    });
  };
  /**
   * sets added voucher, to open the popup
   * @param voucherAdded voucher code string that was added to trigger voucher popup
   */
  const setVoucherAdded = (voucherAdded: string) => {
    setState((prevState) => {
      if (!prevState) {
        return null;
      }

      return {...prevState, voucherAdded};
    });
  };

  const updateCurrencyCode = async (cart: Cart) => {
    const selectCurrencyForm = document.querySelector<ICurrencySelector>("#SelectCurrencyModal > form");
    if (selectCurrencyForm) {
      const preselectedCurrency = selectCurrencyForm.CurrencyCode.selectedOptions[0].value as CurrencyCode;
      if (preselectedCurrency && cart.currencyCode !== preselectedCurrency) {
        await cart.put({currencyCode: preselectedCurrency});
      }
    }
  };

  const cartContextNoCart: ICartContextNoCart = {
    requestCart: async (): Promise<IRequestCart> => {
      const activeCountry = window.c_ActiveCountry;
      const activeCurrency = window.c_ActiveCurrency as CurrencyCode;

      return await CartFactory.generateCart(getShopId(), customer.visitorId, {
        countryCode: activeCountry,
        currencyCode: activeCurrency,
        promotion: UrlSearchParamsHelper.promotions,
      }).then(async (cart) => {
        await updateCurrencyCode(cart);

        // this must be called by the callback once the fallback is done
        const done = () => {
          updateCart(cart);
        };

        return {cart, done};
      });
    },
    setItemsAdded,
    itemsAdded: [],

    setVoucherAdded,
    voucherAdded: "",

    setIsCartVisible: (isCartVisible: boolean) =>
      setState((prevState) => {
        if (!prevState) {
          return null;
        }

        return {...prevState, isCartVisible};
      }),
    isCartVisible: false,
  };

  const [state, setState] = useState<IUnsafeCartContext>(cartContextNoCart);
  const {customer} = useCustomerContext();

  // We need this ref to use it in clean up useEffect callback
  const cartRef = React.useRef<Cart | null>(null);

  // region check for navigation in cart or checkout
  const params = new URLSearchParams(window.location.search);
  // when this is present we assume a cart navigation which leads to an visible cart / checkout by default
  const goToStepGetParameter = params.get("gotoStep");
  const isNavigationStep = !!goToStepGetParameter;
  // endregion

  const setPartialCartContextState = (partialState: Partial<ICartContext>) => {
    setState((previousState) => {
      if (!isCart(previousState)) {
        return null;
      }

      return {...previousState, ...partialState};
    });
  };

  const refreshCart = () => {
    setPartialCartContextState({});
  };

  useEffect(() => {
    if (UrlSearchParamsHelper.containsParams()) {
      void cartContextNoCart.requestCart().then(async ({cart, done}) => {
        const itemsAdded: number[] = [];
        let voucherAdded = "";
        UrlSearchParamsHelper.itemIds.map(async (item) => {
          // this assumes that the following promise resolves without error - this is not defensive coding, it's mvp
          itemsAdded.push(item.itemId);
          await cart.addItem(item.itemId, item.quantity);
        });

        if (UrlSearchParamsHelper.voucherCode) {
          voucherAdded = UrlSearchParamsHelper.voucherCode;
          await cart.addVoucher(UrlSearchParamsHelper.voucherCode);
        }

        if (state && (itemsAdded.length > 0 || voucherAdded !== "")) {
          setState((prevState) => {
            if (!prevState) {
              return null;
            }

            return {...prevState, itemsAdded, voucherAdded};
          });
        }

        done();
      });
    } else {
      void CartFactory.getCartForVisitorId(getShopId(), customer.visitorId).then(async (existingCart) => {
        if (existingCart) {
          await updateCurrencyCode(existingCart);
          updateCart(existingCart);
        }
      });
    }

    // clean up of event listeners to prevent setting state in unmounted components
    return () => {
      if (!cartRef.current) {
        return;
      }
      cartRef.current.emitter.off(refreshCart);
    };
  }, []);

  useEffect(() => {
    if (state?.isCartVisible) {
      Tracking.sendEvent("trackCartPopupOpened");
    }
  }, [state?.isCartVisible]);

  useEffect(() => {
    if (state && state.itemsAdded.length && isCart(state)) {
      const itemAddedId = state.itemsAdded[0];

      const currentPosition = state.cart.positions.find(
        (pos) => pos.reference && pos.reference.itemId !== null && !!pos.item && pos.item.itemId === itemAddedId,
      );

      if (currentPosition?.item) {
        const productData: IProductTrackingData = {
          brand: currentPosition.item.manufacturer,
          id: itemAddedId,
          name: currentPosition.text,
          price: currentPosition.price.amount,
          quantity: currentPosition.quantity,
        };

        Tracking.sendAddToCart(productData, state.cart.currencyCode);
      }
    }

    return;
  }, [state && state.itemsAdded.length]);

  // this is only called when the cart is being initialized
  const updateCart = (cart: Cart): void => {
    cart.emitter.on(refreshCart);
    cartRef.current = cart;

    setState((prevState) => {
      return {
        cart,

        // the following is not really my doing
        isIntoCheckout: (isNavigationStep && isAmazonLoggedIn()) || goToStepGetParameter === "magicLink",
        setIsIntoCheckout: (isIntoCheckout: boolean) => setPartialCartContextState({isIntoCheckout}),

        isCartVisible: prevState ? isNavigationStep || prevState.isCartVisible : false,
        setIsCartVisible: (isCartVisible: boolean) => {
          Sentry.addBreadcrumb({
            category: "step.change",
            level: Severity.Info,
            type: "User Action",
            message: isCartVisible ? "opening cart" : "closing cart",
          });
          setPartialCartContextState({isCartVisible});
        },

        itemsAdded: prevState ? prevState.itemsAdded : [],
        setItemsAdded,

        setVoucherAdded,
        voucherAdded: prevState ? prevState.voucherAdded : "",

        setViewState: (viewState: IViewState | undefined) => setPartialCartContextState({viewState}),
        viewState: {isRegistering: false},
      };
    });
  };

  const {Provider} = unsafeCartContext;

  return <Provider value={state}>{props.children}</Provider>;
};

export default CartContextProvider;
